'use client';

import { motion } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import { useRouter, useSearchParams } from 'next/navigation';

interface CaseStudy {
  id: string;
  title: string;
  industry: string;
  description: string;
  image: string;
}

const CaseStudiesSection = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const industrySlug = searchParams.get('industry') ?? 'all';
  const INDUSTRY_MAP = {
    all: 'all',
    'government-law-enforcement-projects': 'Government & Law Enforcement Projects',
    'fintech-wealth-management': 'Fintech & Wealth Management',
    'education-communication': 'Education & Communication',
    'enterprise-manufacturing-solutions': 'Enterprise & Manufacturing Solutions',
    'retail-community-ai-powered-applications': 'Retail, Community & AI-Powered Applications',
  };
  const selectedIndustry = INDUSTRY_MAP[industrySlug as keyof typeof INDUSTRY_MAP];

  const caseStudies: CaseStudy[] = [
    {
      id: '1',
      title: 'Delhi Police Programs Management App',
      industry: 'Government & Law Enforcement Projects',
      description:
        'Streamlines rally, event, demolition, and intelligence management for Delhi Police officers.',
      image: '/launches/police-management-app-dashboard-with-real-time-com.png',
    },
    {
      id: '2',
      title: 'Madurai Police App',
      industry: 'Government & Law Enforcement Projects',
      description: 'Law enforcement features tailored to Madurai Police Department needs.',
      image: '/launches/madurai-police-app-interface-with-incident-trackin.png',
    },
    {
      id: '3',
      title: 'Train Alarm System',
      industry: 'Government & Law Enforcement Projects',
      description: 'Wildlife-aware railway alerting to prevent forest-area accidents.',
      image: '/launches/railway-train-alarm-system-with-wildlife-detection.png',
    },
    {
      id: '4',
      title: 'Secure Intelligence Management',
      industry: 'Government & Law Enforcement Projects',
      description: 'High-security intelligence management for a government agency.',
      image: '/launches/secure-intelligence-management-system-dashboard.png',
    },
    {
      id: '5',
      title: 'PlusCash',
      industry: 'Fintech & Wealth Management',
      description: 'Wealth management with high-yield funds and on-demand liquidity.',
      image: '/launches/fintech-investment-app-wealth-management-portfolio.png',
    },
    {
      id: '6',
      title: 'Talk2Partners',
      industry: 'Education & Communication',
      description: 'Peer communication practice for Hindi-medium students.',
      image: '/launches/english-learning-app-peer-to-peer-audio-conversati.png',
    },
  ];

  const filteredStudies =
    !selectedIndustry || selectedIndustry === 'all'
      ? caseStudies
      : caseStudies.filter((study) => study.industry === selectedIndustry);

  return (
    <section id="case-studies" className="section-pad section-rule relative z-10 bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          className="mb-8 max-w-2xl sm:mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <p className="mb-2 font-sans text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-primary sm:mb-3 sm:text-xs">
            Proof in production
          </p>
          <h2 className="mb-3 font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            {selectedIndustry === 'all' ? 'Recent launches' : selectedIndustry}
          </h2>
          <p className="text-sm leading-relaxed text-text-secondary sm:text-base">
            {selectedIndustry === 'all'
              ? "Real MVPs we've shipped in 28 days across multiple industries."
              : `Proven solutions built for the ${selectedIndustry} sector.`}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {filteredStudies.map((study, index) => (
            <motion.article
              key={study.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: index * 0.04 }}
              className="group"
            >
              <div className="relative mb-3 aspect-[16/10] overflow-hidden rounded-lg bg-muted">
                <AppImage
                  src={study.image}
                  alt={study.title}
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <p className="mb-1 truncate font-sans text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-primary">
                {study.industry}
              </p>
              <h3 className="mb-1 font-display text-base font-semibold leading-snug text-foreground sm:text-lg">
                {study.title}
              </h3>
              <p className="line-clamp-2 text-sm leading-relaxed text-text-secondary">
                {study.description}
              </p>
            </motion.article>
          ))}
        </div>

        <div className="mt-8 sm:mt-10">
          <button
            onClick={() => router.push('/launches')}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-transparent px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary sm:w-auto"
          >
            See more launches
          </button>
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
